/** @jsx jsx */

import { map } from 'lodash';
import { jsx } from '@emotion/core';
import { Fragment, SyntheticEvent, useCallback } from 'react';

import { useLink } from '@zapier/common-hooks';
import { Button, Margin, Text } from '@zapier/design-system';
import { Icon } from '@zapier/zinnia-icons';

import ClickableTable from 'app/developer-v3/components/ClickableTable';
import ActionsDropdownMenu from 'app/developer-v3/components/ActionsDropdownMenu';
import {
  ACTION_INPUT_ROUTES as INPUT_ROUTES,
  ACTION_INPUT_TYPES as INPUT_TYPES,
  NEW_SERVICE_FIELD,
} from 'app/developer-v3/constants';
import {
  getActionUrl,
  isLastDescendantNodeClicked,
} from 'app/developer-v3/utils';

import type { ActionInputParams } from 'app/developer-v3/types';

type Props = {
  deleteInputField: (inputIndex: string, childInputIndex: number) => void;
  items: Array<{ label: string; key: string }> | [];
  isCreateButtonDisabled: boolean;
  isReadOnly?: boolean;
  params: ActionInputParams;
};

const defaultProps = {
  isReadOnly: false,
  isCreateButtonDisabled: false,
  items: [],
};

const ActionGroupChildInputsTable = (props: Props) => {
  const {
    deleteInputField,
    isCreateButtonDisabled,
    isReadOnly,
    items,
    params,
  } = props;

  const { routeTo } = useLink();

  const onClickRow = useCallback(
    (event: SyntheticEvent<HTMLElement>, index: number) => {
      if (!isLastDescendantNodeClicked(event)) {
        routeTo(
          `${getActionUrl(params)}/input/${params.inputKey}/` +
            `${INPUT_ROUTES[INPUT_TYPES.GROUP]}/${index}`
        );
      }
    },
    [params, routeTo]
  );

  const onClickCreateButton = useCallback(() => {
    routeTo(
      `${getActionUrl(params)}/input/${params.inputKey}/` +
        `${INPUT_ROUTES[INPUT_TYPES.GROUP]}/${NEW_SERVICE_FIELD}`
    );
  }, [params, routeTo]);

  return (
    <Margin margin="40px 0px 0px 0px">
      <Text fontWeight={600} margin="20px 0px 10px 0px" tag="h3" type="Body4">
        Line Items
      </Text>
      <ClickableTable
        body={map(items, (item, index) => [
          { value: item.label, element: item.label },
          { value: item.key, element: item.key },
          {
            value: '',
            element: !isReadOnly && (
              <ActionsDropdownMenu
                items={[
                  {
                    content: (
                      <Fragment>
                        <Icon name="actionEdit" size={16} /> Edit
                      </Fragment>
                    ),
                    onChoose: () => {
                      const url =
                        `${getActionUrl(params)}/input/${params.inputKey}/` +
                        `${INPUT_ROUTES[INPUT_TYPES.GROUP]}/${index}`;
                      routeTo(url);
                    },
                    value: 'edit',
                  },
                  {
                    isConfirmationRequired: true,
                    isDanger: true,
                    content: (
                      <Fragment>
                        <Icon name="actionTrash" size={16} /> Delete
                      </Fragment>
                    ),
                    modalTextContent: `Are you sure you want to delete the input field with key: ${item.key} ?`,
                    modalTitle: 'Delete input field',
                    onChoose: () => {
                      deleteInputField(params.inputKey, index);
                    },
                    value: 'delete',
                  },
                ]}
              />
            ),
          },
        ])}
        header={[{ title: 'Label' }, { title: 'Key' }, { title: '' }]}
        onClickRow={onClickRow}
        rowAriaLabel="Click to edit this action"
      />
      <Margin margin="10px 0px">
        <Button
          isDisabled={isCreateButtonDisabled || isReadOnly}
          isFullWidth={true}
          onPress={onClickCreateButton}
          variant="secondary"
        >
          Add Line Item
        </Button>
      </Margin>
    </Margin>
  );
};

ActionGroupChildInputsTable.defaultProps = defaultProps;

export default ActionGroupChildInputsTable;
